import matrixService from '../services/matrixService';
import { hasExplicitRoomName } from './gematikRoomUtils';
import logger from './logger';

/**
 * Format a single member's name with optional MXID disambiguation.
 *
 * @param {object} member - RoomMember object with rawDisplayName, name, userId
 * @param {Function} needsDisambiguation - Function that checks if a userId needs disambiguation
 * @returns {string} The formatted name, with MXID suffix if disambiguation is needed
 */
function formatMemberName(member, needsDisambiguation) {
  const rawName = member?.rawDisplayName || member?.name || member.userId;
  if (needsDisambiguation(member.userId)) {
    return `${rawName} (${member.userId})`;
  }
  return rawName;
}

/**
 * Join an array of member names with natural language formatting.
 * - 1 name: "Name1"
 * - 2 names: "Name1 and Name2"
 * - 3+ names: "Name1, Name2 and Name3"
 *
 * @param {string[]} names - Array of formatted member names
 * @returns {string} Joined name string
 */
function joinMemberNames(names) {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
}

/**
 * Returns a disambiguated display name for DM rooms when needed.
 *
 * For 2-member DM rooms, appends the MXID suffix if the DM partner's
 * display name is ambiguous.
 *
 * For multi-member DM rooms (3+ members, e.g., when a third user is invited
 * to an existing DM), computes the name from ALL other members with
 * per-member disambiguation.
 *
 * Explicit room names (set by a user via m.room.name or de.gematik.tim.room.name)
 * take priority over disambiguation - if the room has been deliberately named,
 * we honour that name even when the DM partner has a duplicate display name.
 *
 * @param {string} roomId - The room ID to check
 * @param {object} roomObj - The Matrix room object (must support getMember, currentState.getMembers)
 * @param {string} currentName - The current display name
 * @param {string} myUserId - The current user's ID
 * @param {Function} needsDisambiguation - Function that checks if a userId needs disambiguation
 * @returns {string} The (possibly disambiguated) display name
 */
export function getDMDisambiguatedName(roomId, roomObj, currentName, myUserId, needsDisambiguation) {
  const isDM = matrixService.isDMRoom(roomId);
  if (!isDM) return currentName;
  // Explicit room names (set by user) take priority over disambiguation
  const explicit = hasExplicitRoomName(roomObj);
  if (explicit) return currentName;

  // Get all active members excluding current user, sorted by userId for deterministic display
  const otherMembers = (roomObj.currentState?.getMembers()?.filter(m =>
    (m.membership === 'join' || m.membership === 'invite') && m.userId !== myUserId,
  ) || []).sort((a, b) => a.userId.localeCompare(b.userId));

  // Multi-member DM (3+ total members): compute name from ALL other members
  if (otherMembers.length >= 2) {
    const names = otherMembers.map(m => formatMemberName(m, needsDisambiguation));
    const result = joinMemberNames(names);
    logger.debug('[disambiguationUtils] Multi-member DM name:', { roomId, result });
    return result;
  }

  // Original 2-member DM logic (single DM partner)
  const dmUserId = matrixService.getDMUserForRoom(roomId);
  // For incoming DM invites, m.direct may not be populated yet.
  // Fall back to the single other member in the room.
  const targetUserId = (dmUserId && dmUserId !== myUserId)
    ? dmUserId
    : (otherMembers.length === 1 ? otherMembers[0].userId : null);

  if (targetUserId && needsDisambiguation(targetUserId)) {
    const dmMember = roomObj.getMember(targetUserId);
    const rawName = dmMember?.rawDisplayName || dmMember?.name || currentName;
    const result = `${rawName} (${targetUserId})`;
    return result;
  }
  return currentName;
}
